import { useTranslation } from 'react-i18next';

import { COMPARE_PART_LABEL_KEYS } from '@/atoms/deviation-progress.atom';
import type { LayerData } from '@/atoms/layers.atom';
import type { CompareResult } from '@/lib/analyses/compare/availability';
import { comparePartOf, type MergedRun } from '@/lib/analyses/mergeRuns';
import type { AnalysisRunItem } from '@/lib/analyses/runs';
import { translateKey } from '@/lib/analyses/translateKey';

import { RunStatusIcon } from './RunStatusIcon';

export interface RunPart {
  part: CompareResult;
  run: AnalysisRunItem;
}

/** A row's Compare surfaces parts (cut & fill first); empty for any other
 *  analysis. A single compare run has one. */
export const runPartsOf = (run: Pick<MergedRun, 'parts'>): RunPart[] =>
  run.parts.flatMap((p) => {
    const part = comparePartOf(p.type);
    return part ? [{ part, run: p }] : [];
  });

/** Each part with its result layer, when that layer still exists. */
export interface PartResult extends RunPart {
  layer: LayerData | undefined;
}

export const partResultsOf = (run: Pick<MergedRun, 'parts'>, layersById: ReadonlyMap<string, LayerData>): PartResult[] =>
  runPartsOf(run).map((p) => ({ ...p, layer: p.run.resultLayerId ? layersById.get(p.run.resultLayerId) : undefined }));

/** "Cut & fill" / "Deviation map". */
export function usePartLabel(): (part: CompareResult) => string {
  const { t } = useTranslation('project');
  return (part) => translateKey(t, COMPARE_PART_LABEL_KEYS[part]);
}

/**
 * One tag per result of a Compare surfaces row (spec §12.9): the part's name
 * and its own status icon — the icon's shape tells the status, never colour
 * alone. A screen reader hears the tag once, as "Deviation map: Failed" (the
 * visible name and icon are hidden from it); sighted users get the same text
 * as the hover title. Renders nothing for other analyses.
 */
export function RunPartTags({ run }: { run: MergedRun }) {
  const { t } = useTranslation('project');
  const label = usePartLabel();
  const parts = runPartsOf(run);
  if (parts.length === 0) return null;
  return (
    <ul className='mt-1 flex flex-wrap items-center gap-1'>
      {parts.map(({ part, run: p }) => {
        const status = t('analyses.partStatus', {
          part: label(part),
          status: translateKey(t, `analysisLauncher.runs.status.${p.status}`),
        });
        return (
          <li
            key={p.id}
            data-testid={`analysis-run-part-${run.id}-${part}`}
            data-status={p.status}
            title={status}
            className='inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 py-0.5 pl-1 pr-2 text-[11px] font-medium leading-4 text-slate-700 dark:border-stone-700 dark:bg-stone-800/60 dark:text-stone-300'>
            <span aria-hidden className='flex items-center [&>svg]:size-3.5'>
              <RunStatusIcon status={p.status} />
            </span>
            <span aria-hidden data-part-label>
              {label(part)}
            </span>
            <span className='sr-only'>{status}</span>
          </li>
        );
      })}
    </ul>
  );
}
